import { useQuery } from '@apollo/client/react';
import {
  GET_PROJECT_RISKS,
  GET_PROJECT_WITH_RISK,
  GET_PROJECT_ACCOUNT_OWNER,
} from 'domain/projects/newProject/risks/graphql/risksQueries';
import { useMemo } from 'react';
import {
  ProjectRisksResponse,
  ProjectAccountOwnerResponse,
  RisksType,
  UseRiskByIdResult,
} from 'domain/shared/newProject';

export const useProjectRisks = (projectId: string | number) => {
  const { data, loading, error, refetch } = useQuery<ProjectRisksResponse>(GET_PROJECT_RISKS, {
    variables: { id: projectId.toString() },
    skip: !projectId,
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  });

  return {
    risks: data,
    loading,
    error,
    refetch,
  };
};

export const useRiskById = (projectId: string | number | undefined, riskId: string | undefined): UseRiskByIdResult => {
  const { data, loading, error, refetch } = useQuery<ProjectRisksResponse>(GET_PROJECT_WITH_RISK, {
    variables: { projectId: projectId?.toString() },
    skip: !projectId || !riskId,
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  });

  const risk = useMemo(() => {
    if (!data?.baseProject?.risks || !riskId) return null;
    return data.baseProject.risks.find((r: RisksType) => r.id === riskId) || null;
  }, [data, riskId]);

  return {
    risk,
    projectName: data?.baseProject?.name,
    permissions: data?.baseProject?.accessInfo,
    loading,
    error,
    refetch,
  };
};

export const useProjectAccountOwnerAlias = (projectId: string | number | undefined): string | undefined => {
  const { data } = useQuery<ProjectAccountOwnerResponse>(GET_PROJECT_ACCOUNT_OWNER, {
    variables: { projectId: projectId?.toString() },
    skip: !projectId,
    errorPolicy: 'all',
    fetchPolicy: 'cache-and-network',
  });

  return data?.baseProject?.accountData?.ownerAlias;
};
